import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  ActionCodeSettings,
  Auth,
  ConfirmationResult,
  RecaptchaVerifier,
  User,
  UserCredential,
  browserLocalPersistence,
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth';
import { environment } from 'src/environments/environment';

export interface FirebaseLoginResult {
  credential: UserCredential;
  idToken: string;
  provider: 'phone' | 'email';
  firebaseUid: string;
  user: User;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private firebaseApp: FirebaseApp | null = null;
  private authInstance: Auth | null = null;
  private recaptchaVerifier: RecaptchaVerifier | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  async ensureReady(): Promise<void> {
    this.getAuth();
    await this.ensurePersistence();
  }

  hasFirebaseConfig(): boolean {
    const firebaseConfig = (environment as any).firebase || {};
    return Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId);
  }

  isEmailLinkSignIn(url: string): boolean {
    if (!this.isBrowser() || !this.hasFirebaseConfig()) {
      return false;
    }

    return isSignInWithEmailLink(this.getAuth(), url);
  }

  async sendPhoneOtp(phoneNumber: string, containerId = 'recaptcha-container'): Promise<ConfirmationResult> {
    const auth = this.getAuth();
    const verifier = await this.getRecaptchaVerifier(auth, containerId);
    return signInWithPhoneNumber(auth, phoneNumber, verifier);
  }

  async confirmPhoneOtp(confirmationResult: ConfirmationResult, otp: string): Promise<FirebaseLoginResult> {
    const credential = await confirmationResult.confirm(otp.trim());
    return this.buildResult(credential, 'phone');
  }

  async sendEmailSignInLink(email: string, continueUrl: string): Promise<void> {
    const actionCodeSettings: ActionCodeSettings = {
      url: continueUrl,
      handleCodeInApp: true,
    };

    await sendSignInLinkToEmail(this.getAuth(), email.trim().toLowerCase(), actionCodeSettings);
  }

  async completeEmailLinkSignIn(email: string, url: string): Promise<FirebaseLoginResult> {
    const credential = await signInWithEmailLink(this.getAuth(), email.trim().toLowerCase(), url);
    return this.buildResult(credential, 'email');
  }

  async getIdToken(user?: User | null): Promise<string> {
    const currentUser = user ?? this.getAuth().currentUser;
    if (!currentUser) {
      throw new Error('No Firebase user is signed in.');
    }

    return currentUser.getIdToken(true);
  }

  async refreshCurrentUserSession(): Promise<User | null> {
    if (!this.isBrowser()) {
      return null;
    }

    const currentUser = this.getAuth().currentUser;
    if (!currentUser) {
      return null;
    }

    await currentUser.reload();
    return this.getAuth().currentUser;
  }

  async getFreshIdToken(): Promise<string> {
    const refreshedUser = await this.refreshCurrentUserSession();
    return this.getIdToken(refreshedUser);
  }

  async signOut(): Promise<void> {
    if (!this.isBrowser()) {
      return;
    }

    await signOut(this.getAuth());
    this.clearRecaptcha();
  }

  storePendingEmail(email: string): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      globalThis.localStorage.setItem('askiitians_firebase_email', email.trim().toLowerCase());
    } catch {
      // Ignore storage failures.
    }
  }

  getPendingEmail(): string {
    if (!this.isBrowser()) {
      return '';
    }

    try {
      return globalThis.localStorage.getItem('askiitians_firebase_email') || '';
    } catch {
      return '';
    }
  }

  clearPendingEmail(): void {
    if (!this.isBrowser()) {
      return;
    }

    try {
      globalThis.localStorage.removeItem('askiitians_firebase_email');
    } catch {
      // Ignore storage failures.
    }
  }

  clearRecaptcha(): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
  }

  private getAuth(): Auth {
    if (!this.authInstance) {
      this.firebaseApp = this.firebaseApp ?? this.createFirebaseApp();
      this.authInstance = getAuth(this.firebaseApp);
    }

    return this.authInstance;
  }

  private createFirebaseApp(): FirebaseApp {
    if (!this.hasFirebaseConfig()) {
      throw new Error('Firebase configuration is missing from the environment.');
    }

    const firebaseConfig = (environment as any).firebase || {};
    if (getApps().length > 0) {
      return getApp();
    }

    return initializeApp(firebaseConfig);
  }

  private async ensurePersistence(): Promise<void> {
    if (!this.isBrowser()) {
      return;
    }

    await setPersistence(this.getAuth(), browserLocalPersistence);
  }

  private async getRecaptchaVerifier(auth: Auth, containerId: string): Promise<RecaptchaVerifier> {
    if (this.recaptchaVerifier) {
      return this.recaptchaVerifier;
    }

    if (!this.isBrowser()) {
      throw new Error('Firebase phone authentication requires a browser session.');
    }

    this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => undefined,
    });

    await this.recaptchaVerifier.render();
    return this.recaptchaVerifier;
  }

  private async buildResult(credential: UserCredential, provider: 'phone' | 'email'): Promise<FirebaseLoginResult> {
    const user = credential.user;
    const idToken = await this.getIdToken(user);

    return {
      credential,
      idToken,
      provider,
      firebaseUid: user.uid,
      user,
    };
  }
}
